import { useEffect, useState } from "react";
import {
  Control,
  Controller, UseFormGetValues, UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  RegisterOptions,
  FieldPath
} from "react-hook-form";
import { useKeyPress } from "hooks/useKeyPress";
import { ISignUpFieldValues } from "../AuthSignUp";
import { BaseButton } from "components/buttons/BaseButton";
import Captcha from "components/UI/Captcha";
import { apiEmail } from "services";
import { AxiosError } from "axios";
import { IErrorData } from "services/types";
import { TextInput } from "components/inputs/TextInput/TextInput";
import authPopupClasses from "./../AuthPopup.module.scss";

interface IProps {
  next: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  register: UseFormRegister<ISignUpFieldValues>;
  setValue: UseFormSetValue<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

const SignUpUsernameStep = (props: IProps) => {
  const { next, control, trigger, register, setValue, getValues } = props;

  const [errorMessage, setErrorMessage] = useState<null | string>(null);
  const [disabled, setDisabled] = useState(false);
  const [resetCaptcha, setResetCaptcha] = useState<(() => void) | null>(null);

  register<FieldPath<ISignUpFieldValues>>("captcha", { required: true });

  const enterPressed = useKeyPress("Enter");

  const handleNext = async () => {
    if (await trigger(["username", "email", "captcha"])) {
      await sendCode();
    }
  };

  const sendCode = async () => {
    const { username, email, captcha, is_confirm_email } = getValues();

    if (is_confirm_email) {
      next();
      return;
    }

    setDisabled(true);
    setErrorMessage(null);

    try {
      const result = await apiEmail.sendCode({ username, email, captcha });
      setValue<FieldPath<ISignUpFieldValues>>("validation_token", result.token);
      next();
    } catch (e) {
      console.warn(e);
      const message = (e as AxiosError<IErrorData>)?.response?.data?.error?.message || "Service unavailable";
      setErrorMessage(message);
      if (resetCaptcha !== null) {
        resetCaptcha();
      }
    } finally {
      setDisabled(false);
    }
  };

  const handleCaptchaSuccess = (token: string) => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", token);
  };

  const handleCaptchaReset = () => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", "");
  };

  const handleCaptchaRendered = (reset: () => void) => {
    setValue<FieldPath<ISignUpFieldValues>>("captcha", "");
    setResetCaptcha(() => reset);
  };

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
    // eslint-disable-next-line
  }, [enterPressed])

  return (
    <div className={authPopupClasses.ContentContainer}>
      <Controller
        name="username"
        control={control}
        defaultValue=""
        rules={{
          required: "Please come up with a user name.",
          minLength: {
            value: 6,
            message: "User name is too short."
          },
          maxLength: {
            value: 32,
            message: "User name is too long."
          },
          pattern: {
            value: /^[a-z0-9-]{6,}$/,
            message: "Please only use lower case letters and digits."
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <TextInput
            label="Username"
            onChange={async (e) => {
              field.onChange(e);
              await trigger("username");
            }}
            value={field.value}
            error={fieldState.error ? fieldState.error.message : ""}
          />
        )}
      />
      <Controller
        name="email"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Please specify your email address."
          },
          pattern: {
            value: /^[A-Za-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}/,
            message: "Invalid Email!"
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <TextInput
            label="Email"
            onChange={async (e) => {
              field.onChange(e);
              setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", false);
              await trigger("email");
            }}
            value={field.value}
            error={fieldState.error ? fieldState.error.message : ""}
          />
        )}
      />

      <Captcha
        id="captcha"
        onSuccess={handleCaptchaSuccess}
        onFailed={handleCaptchaReset}
        onRendered={handleCaptchaRendered}
      />

      { errorMessage &&
        <div className={authPopupClasses.ErrorMessage}>
          { errorMessage }
        </div>
      }

      <div className={authPopupClasses.Footer}>
        <BaseButton type="button" onClick={handleNext} disabled={disabled}>Next</BaseButton>
      </div>
    </div>
  );
};

export default SignUpUsernameStep;
